'use client';

import { useCheckSpotOnly } from '@flipster/features/src/shared/regulation';
import { css } from '@flipster/styled-system/css';
import { hstack, stack, vstack } from '@flipster/styled-system/patterns';

import earn from '@web/assets/images/shared/earn.webp';
import fast from '@web/assets/images/shared/fast.webp';
import security from '@web/assets/images/shared/security.webp';
import trading from '@web/assets/images/shared/trading.webp';
import trustedPlatform from '@web/assets/images/shared/trusted-platform.webp';
import zeroSpread from '@web/assets/images/shared/zero-spread.webp';

import SessionNudgeButton from './SessionNudgeButton';

const BenefitSection = () => {
  const isSpotOnly = useCheckSpotOnly();

  return (
    <section className={stack({ gap: 40 })}>
      <div className={vstack({ gap: 38 })}>
        {(isSpotOnly ? spotOnlyBenefits : benefits).map((benefit) => {
          return (
            <BenefitItem
              key={benefit.title}
              image={benefit.image}
              title={benefit.title}
              description={benefit.description}
            />
          );
        })}
      </div>
      <SessionNudgeButton />
    </section>
  );
};

type BenefitItemProps = {
  image: string;
  title: string;
  description: string;
};

const BenefitItem = ({ image, title, description }: BenefitItemProps) => {
  return (
    <div className={hstack({ gap: 17, alignItems: 'flex-start', w: '100%' })}>
      <img src={image} alt={title} className={css({ w: 44, h: 44 })} />
      <div className={vstack({ gap: 8, color: 'content.default.level-4', alignItems: 'flex-start' })}>
        <h3 className={css({ textStyle: 'Title/M' })}>{title}</h3>
        <p className={css({ textStyle: 'Text/L/Regular' })}>{description}</p>
      </div>
    </div>
  );
};

export default BenefitSection;

const benefits: BenefitItemProps[] = [
  {
    image: zeroSpread.src,
    title: 'Zero spreads on major pairs',
    description: 'No spread. Just speed.',
  },
  {
    image: fast.src,
    title: 'Instant execution',
    description: 'Trade perps with lightning-fast fills.',
  },
  {
    image: earn.src,
    title: 'Earn while your trade',
    description: 'Earn yield on USDT even as you trade.',
  },
];

const spotOnlyBenefits = [
  {
    image: trading.src,
    title: 'Fast, easy trading',
    description: 'Buy and sell crypto in just a few taps.',
  },
  {
    image: security.src,
    title: 'Protected by design',
    description: 'Security and compliance built into every step.',
  },
  {
    image: trustedPlatform.src,
    title: 'Trusted access',
    description: 'A platform you can rely on, wherever you are in your crypto journey.',
  },
];
